import React, {FunctionComponent, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Colors, Icon, Switch} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import debounce from 'lodash/debounce';

import {shareWithWorkspace} from '../../../../../store/actions/sharingSettingsActions';
import {ReportSharingSettings, SharingSettingsTypes} from '../../../../../models';
import {useCurrentWorkspace} from '../../../../../hooks';
import {getShareWithWorkspaceOption, shareWithWorkspaceChecked} from './utils';
import SelectAction from './SelectAction';

interface WithWorkspaceProps {
  uid: string;
  sharingSettings: ReportSharingSettings;
  onChange?: (value: boolean) => void;
}

const LabelElement = ({name}: any) => {
  return (
    <>
      <Icon
        icon={IconNames.OFFICE}
        color={Colors.GRAY1}
      /> Everyone at {name}
    </>
  )
}

const WithWorkspace: FunctionComponent<WithWorkspaceProps> = ({
  uid,
  sharingSettings,
}: WithWorkspaceProps) => {
  const [checked, setChecked] = useState(shareWithWorkspaceChecked(sharingSettings));
  const [selected, setSelected] = useState(getShareWithWorkspaceOption(sharingSettings));

  const currentWorkspace = useCurrentWorkspace();
  const dispatch = useDispatch();

  const workspaceName = currentWorkspace?.name || 'default';
  const organizationName = currentWorkspace?.organization.name || '';

  // `default` is used for the default workspace
  let everyoneAt = workspaceName !== 'default' ? workspaceName : organizationName;

  const onChange = (action: string) => {
    dispatch(shareWithWorkspace(SharingSettingsTypes.REPORT, uid, action));
  }
  const debounceOnChange = useMemo(() => debounce(onChange, 400), [ ]);

  const onSwitchChange = (event: any) => {
    const value = event.target.checked;

    // if value is false (shared not checked) - we don't send the permission name in order to remove sharing settings
    debounceOnChange(value ? selected : '');
    setChecked(value);
  }

  const onShareWithWorkspace = (value: any) => {
    const action = value.value;
    setSelected(action);
    debounceOnChange(action);
  }

  return (
    <div className="share-with-item">
      <Switch
        inline={true}
        large={true}
        labelElement={<LabelElement name={everyoneAt}/>}
        checked={checked}
        onChange={onSwitchChange}
      />
      <SelectAction
        selected={selected}
        disabled={!checked} /* if not checked */
        onChange={onShareWithWorkspace}
      />
    </div>
  )
};

export default WithWorkspace;
